import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	ContextMenuLinkItem,
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PermissionName } from "../../../shared/types/permission";
import { UserSelectOption } from "../../../shared/ui/UserSelectOption";
import { campaignService } from "../shared/api/campaignService";
import { ICampaignIndexResource } from "../shared/types";
import { CampaignCreateDrawer, CampaignUpdateDrawer } from "./CampaignDrawer";

export const CampaignList: FC = () => {
	const { t } = useTranslation();
	const canCreate = useHasPermission<PermissionName>(["campaign.create"]);
	const canUpdate = useHasPermission<PermissionName>(["campaign.update"]);

	const crudOperations = useLaravelCrudEntity(campaignService, {
		entityName: t("Campaign"),
		messages: {
			create: t("Campaign was successfully created."),
			update: t("Campaign was successfully updated."),
			delete: t("Campaign was successfully deleted."),
		},
	});

	const dataListColumns: IDrawerDataListColumn<ICampaignIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: t("Created by"),
				type: "value",
				valueKey: "createdBy.name",
				render: (x) => <UserSelectOption user={x.createdBy} isSelected={false} />,
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Edit campaign")}
							label={t("Edit campaign")}
							action={openDrawer}
							icon="edit"
							shortcut="primary"
						/>
						<ContextMenuLinkItem
							to={`${id}`}
							title={t("View campaign")}
							label={t("View campaign")}
							icon="eye"
							shortcut="primary"
						/>
						<ContextMenuDeleteItem
							action={() => crudOperations.deleteEntity(id)}
							title={t("Delete campaign")}
							label={t("Delete campaign")}
							heading={t("Delete")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
						>
							{t("Are you sure you want to delete this campaign?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[crudOperations, t],
	);

	return (
		<PaginatedDrawerAutoDataList
			canCreate={canCreate}
			canUpdate={canUpdate}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(campaignService.listKey, campaignService.index)}
			renderCreateDrawer={(drawerProps) => (
				<CampaignCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(entityFormState)}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<CampaignUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, entityFormState)}
				/>
			)}
			translations={{
				title: t("Campaigns"),
				empty: t("No campaigns available"),
			}}
			initialSearch={{ search: "", searchFields: [] /* TODO:  ["name"]*/ }}
		/>
	);
};
